/**
 * Microsoft Contacts (Microsoft 365, Outlook.com, Hotmail, Live) — API client for the Select Recipients modal.
 * Backend: sendlinxapi MicrosoftContactsController (api/user/contacts/microsoft/...).
 *
 * Sign-in runs in a popup so the Send page (typed recipients, message, dropped files)
 * is never reloaded. The popup returns to /integrations/microsoft/callback, which posts
 * {code, state} to the API with the user's normal auth headers and then tells this
 * window the result (postMessage + a localStorage event as a fallback, because
 * Microsoft's sign-in pages can cut the popup's link to its opener).
 * Microsoft tokens never reach the browser.
 *
 * Set REACT_APP_MS365_MOCK=true to run the UI on in-memory sample data. Never in production.
 */
import axios from "axios";
import Config from "../../../../../Config";

export const MS365_MESSAGE = "sendlinx:ms365";
export const MS365_RESULT_KEY = "sendlinx_ms365_result";
export const MS365_RETURN_KEY = "sendlinx_ms365_return";
export const MS365_CALLBACK_PATH = "/integrations/microsoft/callback";

export const isMs365MockMode = () => {
    try{ return process.env.REACT_APP_MS365_MOCK === "true"; }catch(e){ }
    return false;
};

const unwrap = (res) => {
    const data = res && res.data ? res.data : {};
    if(data.code === undefined || data.code === 200){
        return data.body !== undefined ? data.body : data;
    }
    throw (data.body || { message: "Invalid response from api." });
};
const unwrapError = (err) => {
    if(err && err.response && typeof(err.response) === "object" && err.response.data){
        return err.response.data.body || err.response.data;
    }
    return err;
};
const request = (method, path, { data = null, params = null, source = null } = {}) => {
    const options = { url: Config.getApiUrl()+path, method: method, headers: Config.getApiHeaders() };
    if(data !== null){ options.data = JSON.stringify(data); }
    if(params !== null){ options.params = params; }
    if(source !== null){ options.cancelToken = source.token; }
    return axios(options).then(unwrap).catch(err => { throw unwrapError(err); });
};

/* ---------------- mock store ---------------- */
const MOCK_PEOPLE = [
    ["Emma Harris", "emma.harris@acmecorp.example", "Acme Corp", "c"], ["James Dalton", "james.dalton@acmecorp.example", "Acme Corp", "c"],
    ["Sophia Lee", "sophia.lee@brightway.example", "Brightway Ltd", "c"], ["Michael Tan", "michael.tan@globaltech.example", "Global Tech", "v"],
    ["Olivia Bennett", "olivia.bennett@acmecorp.example", "Acme Corp", "c"], ["Daniel Roberts", "daniel.roberts@innovatech.example", "Innovatech", "v"],
    ["Priya Nair", "priya.nair@northwind.example", "Northwind", "c"], ["Lucas Moreau", "lucas.moreau@fabrikam.example", "Fabrikam", "v"],
    ["Hannah Weber", "hannah.weber@contoso.example", "Contoso", "c"], ["Omar Haddad", "omar.haddad@tailspin.example", "Tailspin", "f"],
    ["", "accounts@woodgrove.example", "Woodgrove Bank", "v"], ["Grace Kim", "grace.kim@litware.example", "", "f"]
];
const mock = {
    status: { provider: "microsoft", configured: true, status: "disconnected", connected: false, account: null, connectedAt: null, lastSyncedAt: null },
    folders: [{ id: "default", name: "Contacts", default: true }, { id: "f-family", name: "Family" }, { id: "f-vendors", name: "Vendors" }],
    rows: (() => {
        const rows = [];
        const folderName = { c: "Contacts", v: "Vendors", f: "Family" };
        MOCK_PEOPLE.forEach(([name, email, company, folder], i) => {
            const emails = i === 2 ? [email, "sophia@home.example"] : [email];
            emails.forEach((e, n) => rows.push({ id: "m"+i+"-"+n, source: "microsoft365", sourceId: "m"+i, name, firstName: name.split(" ")[0] || "",
                lastName: name.split(" ").slice(1).join(" "), company, email: e, emailIndex: n + 1, emailCount: emails.length, folder: folderName[folder] }));
        });
        for(let i = 1; i <= 48; i++){
            const n = String(i).padStart(2, "0");
            rows.push({ id: "g"+i, source: "microsoft365", sourceId: "g"+i, name: "Sample Contact "+n, firstName: "Sample", lastName: "Contact "+n,
                company: i % 3 === 0 ? "Example Co" : "", email: "sample"+n+"@example.com", emailIndex: 1, emailCount: 1, folder: "Contacts" });
        }
        return rows;
    })()
};
const copy = (v) => JSON.parse(JSON.stringify(v));
const delay = (v, ms = 250) => new Promise(resolve => setTimeout(() => resolve(copy(v)), ms));
const mockContacts = (query) => {
    const q = String(query.q || "").trim().toLowerCase();
    const folder = query.folder || "";
    const folderName = (mock.folders.find(f => f.id === folder) || {}).name;
    let rows = mock.rows.filter(r => (!folder || r.folder === folderName) &&
        (!q || (r.name+" "+r.email+" "+r.company).toLowerCase().includes(q)));
    const key = query.sort || "name";
    const dir = query.dir === "desc" ? -1 : 1;
    rows = rows.slice().sort((a, b) => {
        const av = key === "name" ? (a.name || a.email) : a[key];
        const bv = key === "name" ? (b.name || b.email) : b[key];
        if((av === "") !== (bv === "")){ return av === "" ? 1 : -1; }
        return av.localeCompare(bv) * dir || a.email.localeCompare(b.email);
    });
    const limit = query.limit || 50;
    const page = query.page || 1;
    return { items: rows.slice((page - 1) * limit, page * limit), total: rows.length, page, limit, hasMore: page * limit < rows.length,
        truncated: false, syncedAt: new Date().toISOString(), folder };
};

/* ---------------- API ---------------- */
const BASE = "user/contacts/microsoft";

const Ms365Api = {
    status(){
        if(isMs365MockMode()){ return delay(mock.status, 150); }
        return request("GET", BASE+"/status");
    },
    /** Returns { authorizeUrl, expiresIn }. */
    connect(){
        if(isMs365MockMode()){ return delay({ authorizeUrl: "about:blank", expiresIn: 600 }, 100); }
        return request("POST", BASE+"/connect", { data: {} });
    },
    /** Called by the callback page with Microsoft's ?code&state (or ?error). Returns the new status. */
    completeConnect({ code, state, error }){
        if(isMs365MockMode()){ return mockConnect(); }
        return request("POST", BASE+"/callback", { data: { code: code || null, state: state || null, error: error || null } });
    },
    folders(){
        if(isMs365MockMode()){ return mock.status.connected ? delay({ folders: mock.folders }) : Promise.reject({ message: "Connect your Microsoft account first.", reason: "not_connected" }); }
        return request("GET", BASE+"/folders");
    },
    /** query: { q, folder, sort: "name"|"email"|"company", dir: "asc"|"desc", page, limit, refresh } */
    contacts(query = {}, source = null){
        if(isMs365MockMode()){ return mock.status.connected ? delay(mockContacts(query), 350) : Promise.reject({ message: "Connect your Microsoft account first.", reason: "not_connected" }); }
        const params = {};
        Object.keys(query).forEach(k => { if(query[k] !== "" && query[k] !== null && query[k] !== undefined){ params[k] = query[k]; } });
        return request("GET", BASE, { params, source });
    },
    disconnect(){
        if(isMs365MockMode()){
            mock.status = { ...mock.status, status: "disconnected", connected: false, account: null, connectedAt: null, lastSyncedAt: null };
            return delay(mock.status);
        }
        return request("DELETE", BASE+"/connection");
    }
};

const mockConnect = () => {
    const now = new Date().toISOString();
    mock.status = { ...mock.status, status: "connected", connected: true, account: { email: "matt.george@acmecorp.example", name: "Matt George" }, connectedAt: now, lastSyncedAt: now };
    return delay(mock.status, 400);
};

/* ---------------- sign-in popup ---------------- */

/**
 * Opens the sign-in popup. Must be called directly from the click handler (before any
 * await) so browsers don't block it. Returns null when the popup was blocked.
 */
export const openSignInPopup = () => {
    if(isMs365MockMode()){ return { mock: true, closed: false, close(){ this.closed = true; } }; }
    const width = 520;
    const height = 680;
    const left = Math.max(0, Math.round((window.screenX || 0) + ((window.outerWidth || width) - width) / 2));
    const top = Math.max(0, Math.round((window.screenY || 0) + ((window.outerHeight || height) - height) / 2));
    let popup = null;
    try{
        popup = window.open("", "sendlinx_ms365_signin", "popup=yes,width="+width+",height="+height+",left="+left+",top="+top);
    }catch(e){ popup = null; }
    if(!popup){ return null; }
    try{
        popup.document.title = "Connecting your Microsoft account…";
        popup.document.body.style.cssText = "font-family:system-ui,sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;color:#444";
        popup.document.body.textContent = "Opening Microsoft sign-in…";
    }catch(e){ }
    return popup;
};

/** Sign-ins left open longer than the server-side state lives (10 minutes) are closed. */
const SIGN_IN_TIMEOUT_MS = 10 * 60 * 1000;

/** Closes the sign-in popup (Cancel, or a result arrived but the popup couldn't close itself). */
export const closeSignInPopup = (popup) => {
    try{ if(popup && !popup.mock && !popup.closed){ popup.close(); } }catch(e){ }
};

/**
 * Sends the popup to Microsoft and resolves when the callback page reports back or the
 * popup is closed: { ok, message, status } or { closed: true } ({ closed: true, timedOut: true }
 * after 10 minutes). Microsoft's own pages (for example "Need admin approval") never come
 * back on their own, so the caller offers Cancel, which closes the popup.
 */
export const runSignIn = (popup) => {
    if(popup && popup.mock){
        return mockConnect().then(status => ({ ok: true, status }));
    }
    return Ms365Api.connect().then(data => {
        if(!popup || popup.closed){ return { closed: true }; }
        popup.location.href = data.authorizeUrl;
        return new Promise(resolve => {
            let done = false;
            const finish = (result) => {
                if(done){ return; }
                done = true;
                window.removeEventListener("message", onMessage);
                window.removeEventListener("storage", onStorage);
                clearInterval(timer);
                clearTimeout(timeout);
                // The callback page closes itself; this is the backup if it couldn't.
                if(result && result.type === MS365_MESSAGE){ closeSignInPopup(popup); }
                resolve(result);
            };
            const onMessage = (event) => {
                if(event.origin !== window.location.origin || !event.data || event.data.type !== MS365_MESSAGE){ return; }
                finish(event.data);
            };
            const onStorage = (event) => {
                if(event.key !== MS365_RESULT_KEY || !event.newValue){ return; }
                try{ finish(JSON.parse(event.newValue)); }catch(e){ }
            };
            window.addEventListener("message", onMessage);
            window.addEventListener("storage", onStorage);
            const timer = setInterval(() => {
                let closed = true;
                try{ closed = popup.closed; }catch(e){ closed = false; }
                if(closed){ setTimeout(() => finish({ closed: true }), 400); clearInterval(timer); }
            }, 500);
            const timeout = setTimeout(() => { closeSignInPopup(popup); finish({ closed: true, timedOut: true }); }, SIGN_IN_TIMEOUT_MS);
        });
    }).catch(err => {
        try{ if(popup && !popup.closed){ popup.close(); } }catch(e){ }
        throw err;
    });
};

/** Popup blocked: sign in in this tab and come back to the Send page afterwards. */
export const signInSameTab = () => {
    return Ms365Api.connect().then(data => {
        try{ sessionStorage.setItem(MS365_RETURN_KEY, window.location.pathname || "/"); }catch(e){ }
        window.location.assign(data.authorizeUrl);
    });
};

export default Ms365Api;
